<template>
  <div class="groupedit">
    <div class="space-between-row">
      <div class="left-outer">
        <forminput
          v-model="group.name"
          v-validate="'required'"
          :hint="$t('groups.name')"
          name="group.name"
          :required="true"
          :is-valid="!errors.has('group.name')"
        />
      </div>
      <div class="simple-text right-outer">
        <div class="cap-label">
          {{ $t('groups.language') }}
        </div>
        <pillbox
          :items="languages"
          :selected="selectedlanguages"
          @change="onLanguageChanged"
        >
        </pillbox>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <textareainput
          v-model="group.description"
          :title="$t('groups.description')"
          :rows="5"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-12 col-sm-6 col-md-4">
        <sprdropdownnumberkey
          v-model="group.category"
          v-validate="'required'"
          :title="$t('groups.category')"
          name="group.category"
          :items="groupCategories"
          :required="true"
          :is-valid="!errors.has('group.category')"
        />
      </div>
    </div>
    <div class="row">
      <div
        v-if="group.groupType === 1 && externalFormsAvailable"
        class="col-12 col-sm-6 col-md-4"
      >
        <div class="cap-label">
          {{ $t('externalForm.addLinkTitle') }}
        </div>
        <dropdowninput
          v-model="newSelectedExternalForm"
          :title="$t('externalForm.addLinkTitle')"
          name="externalForm"
          :items="filteredExternalForms"
          :required="false"
          is-valid
          @change="onExternalFormSelected"
        />
      </div>
    </div>
    <div class="row">
      <div
        v-if="group.groupType === 1 && externalFormsAvailable"
        class="row selected-external-forms-list mt-2"
      >
        <SelectedExternalForm
          v-for="form in selectedExternalForms"
          :key="form.id"
          :external-form="form"
          @remove="removeExternalForm(form.id)"
        />
      </div>
    </div>
    <div class="row national">
      <div class="col-12">
        <simplecheckbox
          v-model="group.isAllLocations"
          :title="$t('groups.isAllLocations')"
          :aria-label="$t('groups.isAllLocations')"
        />
      </div>
    </div>
    <div v-if="isActionGroup()" class="row national">
      <div class="col-12">
        <simplecheckbox
          v-model="group.isOnline"
          :title="$t('groups.isOnline')"
          :aria-label="$t('groups.isOnline')"
        />
      </div>
    </div>
    <!-- Group Organizer -->
    <div v-if="isActionGroup()">
      <div class="row grouporganizer">
        <div class="col-12">
          <groupOrganizer
            v-validate="'hasOptionalGroupOrganizer'"
            :hint="$t('userAdmin.groupOrganizer')"
            name="userAdmin.groupOrganizer"
            :value="group.groupOrganizer"
            :required="false"
            :is-valid="!errors.has('userAdmin.groupOrganizer')"
            @input="onGroupOrganizerChanged"
          />
        </div>
      </div>
    </div>
    <div class="row button-row">
      <div class="col-12">
        <div class="d-flex justify-content-end">
          <sprbutton
            type="submit"
            :size="1"
            :title="$t('groups.save')"
            icon="chevron-right"
            class="spr-redborder button-width"
            click-event="saveGroup"
            @saveGroup="onSaveGroup"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import * as _ from 'lodash'
import sprbutton from '../SPRButton.vue'
import forminput from '../SPRSimpleText.vue'
import textareainput from '../SPRSimpleTextArea.vue'
import simplecheckbox from '../SPRSimpleCheckbox.vue'
import groupOrganizer from '../SPRGroupOrganizersDropdown.vue'
import sprdropdownnumberkey from '../SPRDropdownNumberKey.vue'
import dropdowninput from '../../components/common/Dropdown/SingleSelectDropdown.vue'

import { ExternalFormUpdateDto } from '@/types'
import SelectedExternalForm from '@/components/useradmin/SelectedExternalForm.vue'

import pillbox from '../common/Pill/PillBox.vue'

import { Validator } from 'vee-validate'
import hasOptionalGroupOrganizer from '../../mixins/validator-hasOptionalGroupOrganizer.js'

Validator.extend('hasOptionalGroupOrganizer', hasOptionalGroupOrganizer)

// the data object
export interface TemplateComponentData {
  updated: boolean
  languages: any
  externalForms: Array<ExternalFormUpdateDto>
  selectedExternalForm: any
  selectedExternalForms: Array<{ id: number; text: string }>
  newSelectedExternalForm: { id: number; text: string } | null
  selectedlanguages: any
  groupCategories: any
}

export default Vue.extend({
  name: 'GroupEdit',
  components: {
    sprbutton,
    textareainput,
    forminput,
    simplecheckbox,
    groupOrganizer,
    dropdowninput,
    sprdropdownnumberkey,
    SelectedExternalForm,
    pillbox,
  },
  props: {
    group: {
      type: Object,
      default: null,
    },
  },
  data(): TemplateComponentData {
    return {
      updated: false,
      languages: [],
      externalForms: [],
      selectedExternalForm: undefined,
      selectedExternalForms: [],
      newSelectedExternalForm: null,
      selectedlanguages: [],
      groupCategories: null,
    }
  },
  computed: {
    isOnline(): boolean {
      return this.group.isOnline
    },
    externalFormsAvailable(): boolean {
      return this.externalForms && this.externalForms.length > 0
    },
    filteredExternalForms(): Array<{ id: number; text: string }> {
      return this.externalForms
        .filter(
          (form) =>
            !this.selectedExternalForms.some((sel) => sel.id === form.id)
        )
        .map((form) => ({ id: form.id, text: form.name }))
    },
  },
  mounted() {
    const lang = this.$store.state.customerLanguages
    this.languages = this.$store.state.groupLanguages
      .filter((l) => l.id !== 0)
      .map((v) => {
        return {
          id: v.id,
          text:
            'actionLanguage.' +
            lang.find(
              (l) => l.displayName.toUpperCase() === v.text.toUpperCase()
            ).isoCode,
        }
      })
    this.selectedlanguages = this.languages.filter((v) =>
      v.id === null || v.id === 0
        ? false
        : (v.id & this.group.languages) === v.id
    )
    if (this.group.externalForms?.length) {
      this.selectedExternalForms = this.group.externalForms.map((f) => {
        return { id: f.id, text: f.name }
      })
    }
  },
  created() {
    this.getCategories()
    this.fetchExternalForms()
  },
  methods: {
    fetchExternalForms() {
      try {
        const url =
          this.$store.state.apiBases.externalformservice +
          this.$store.state.apiEndpoints.externalformsall[0]
        const response = this.$http.get(url).then((response) => {
          this.externalForms = response.data as Array<ExternalFormUpdateDto>
          if (this.group.externalForm) {
            this.selectedExternalForm = this.group.externalForm.id.toString()
          } else {
            this.selectedExternalForm = '-1'
          }
        })
      } catch (error) {
        console.error('Error fetching external forms:', error)
      }
    },
    onExternalFormSelected(selected: { id: number; text: string }) {
      if (!selected || typeof selected.id !== 'number') return

      // Check if already added
      if (this.selectedExternalForms.some((f) => f.id === selected.id)) return

      // Add to selected list
      this.selectedExternalForms.push({ id: selected.id, text: selected.text })

      // Sync with group.externalForms
      const fullForm = this.externalForms.find((f) => f.id === selected.id)
      if (fullForm) {
        this.group.externalForms.push(fullForm)
      }

      // Reset selected item
      this.newSelectedExternalForm = null
    },
    removeExternalForm(id: number) {
      this.selectedExternalForms = this.selectedExternalForms.filter(
        (f) => f.id !== id
      )

      this.group.externalForms = this.group.externalForms.filter(
        (f) => f.id !== id
      )
    },
    onLanguageChanged(e) {
      this.group.languages = e.reduce((b, v) => b + v.id, 0)
      this.selectedlanguages = e
    },
    isActionGroup() {
      return this.group.groupType === 1 // public enum GroupType: 1-ActionGroup, 2-StandardGroup (OpenGroup)
    },
    getCategories() {
      var self = this
      this.groupCategories = _.map(
        this.$store.state.eventCategories,
        function (val, key) {
          return { value: val.id, text: self.$t('eventCategory.' + val.text) }
        }
      )
    },
    onGroupOrganizerChanged(groupOrganizer) {
      this.group.groupOrganizer.groupOrganizerType =
        groupOrganizer.groupOrganizerType
      this.group.groupOrganizer.organizerId = groupOrganizer.organizerId
    },
    onSaveGroup() {
      var self = this
      this.$validator.validateAll().then((result) => {
        if (result) {
          self.$emit('saveGroup', self.group)
        }
      })
    },
  },
})
</script>

<style lang="scss">
@import '~@/assets/scss/_variables.scss';
.row {
  display: flex;
}
.space-between-row {
  display: flex;
  justify-content: space-between;
}
.left-outer {
  padding-left: 15px;
}
.right-outer {
  margin-right: 20px;
}
.cap-label {
  margin-bottom: 8px;
}
.multiselect {
  margin: 0px;
  min-height: '';
}
.multiselect__tags {
  min-height: 37px;
  border: solid 1px #dddddd;
}
.multiselect__single {
  font-size: 16px;
  font-family: 'Signa-Light', Sans-Serif;
}
.groupedit {
  .cancel-button {
    margin-right: 5px;
  }
  .update-success {
    color: $brand-success;
    display: inline-block;
    margin-right: 10px;
    margin-top: 15px;
  }

  .national {
    margin-left: 5px;
  }

  .update-leave-active {
    transition: opacity 0.5s;
  }
  .update-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
}
</style>
